import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Badge, Card } from '@/components/ui';
import { PLAN_CONFIG, FEATURE_LABELS } from '@/modules/admin/companies/types';
import type { SubscriptionPlanCode } from '@/modules/admin/companies/types';

const PLANS = [
  {
    code: 'free' as SubscriptionPlanCode,
    price_monthly: 0,
    price_yearly: 0,
    description: 'Dành cho cá nhân hoặc đội ngũ nhỏ trải nghiệm nền tảng.',
    limits: { projects: 1, users: 3, assets: 50 },
    features: ['projects_basic', 'maintenance_basic', 'mobile_view'],
  },
  {
    code: 'basic' as SubscriptionPlanCode,
    price_monthly: 990000,
    price_yearly: 9900000,
    description: 'Phù hợp cho các doanh nghiệp đang bắt đầu số hóa quy trình.',
    limits: { projects: 5, users: 15, assets: 500 },
    features: ['boq', 'progress', 'inventory', 'quality', 'approvals', 'bulk_ops', 'export_pdf'],
  },
  {
    code: 'pro' as SubscriptionPlanCode,
    price_monthly: 2490000,
    price_yearly: 24900000,
    popular: true,
    description: 'Gói tối ưu cho các dự án quy mô lớn và chuyên nghiệp.',
    limits: { projects: 20, users: 50, assets: 5000 },
    features: ['projects_full', 'maintenance_full', 'predictive', 'cost_tracking', 'sla', 'lifecycle', 'kpi_dashboard', 'routes', 'contracts', 'export_all', 'api_access'],
  },
  {
    code: 'enterprise' as SubscriptionPlanCode,
    price_monthly: 5990000,
    price_yearly: 59900000,
    description: 'Giải pháp toàn diện cho tập đoàn lớn với nhu cầu tùy biến cao.',
    limits: { projects: -1, users: -1, assets: -1 },
    features: ['white_label', 'custom_domain', 'priority_support', 'audit_advanced', 'compliance'],
  },
];

export default function PricingPage() {
  const [isYearly, setIsYearly] = useState(true);
  const navigate = useNavigate();

  const handleSelectPlan = (planCode: SubscriptionPlanCode) => {
    navigate(`/auth/register-company?plan=${planCode}&cycle=${isYearly ? 'yearly' : 'monthly'}`);
  };

  const formatPrice = (price: number) => {
    if (price === 0) return 'Miễn phí';
    return (price / 1000).toLocaleString('vi-VN') + 'k';
  };

  return (
    <div className="min-h-screen bg-slate-50 py-12 px-4 md:py-24 overflow-hidden relative">
      {/* Decorative BG */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-emerald-500/5 rounded-full blur-[120px] -z-10" />

      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <Badge variant="custom" className="bg-emerald-100 text-emerald-700 border-none px-4 py-1 rounded-full font-bold mb-6">
            Gói dịch vụ Landscape
          </Badge>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 mb-6 tracking-tight" style={{ fontFamily: "'Playfair Display', serif" }}>
            Giải pháp quản lý <br /><span className="text-emerald-600">tăng trưởng bền vững</span>
          </h1>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto mb-10">
            Chọn gói dịch vụ phù hợp để tối ưu hóa quy trình thi công và bảo dưỡng cảnh quan cho tổ chức của bạn.
          </p>

          {/* Billing Switch */}
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className={`text-sm font-bold ${!isYearly ? 'text-slate-900' : 'text-slate-400'}`}>Hàng tháng</span>
            <button
              onClick={() => setIsYearly(!isYearly)}
              className="w-14 h-8 bg-slate-200 rounded-full relative p-1 transition-colors cursor-pointer"
            >
              <div className={`w-6 h-6 bg-white rounded-full shadow-md transition-all absolute top-1 ${isYearly ? 'left-7 bg-emerald-500' : 'left-1'}`} />
            </button>
            <div className="flex items-center gap-2">
              <span className={`text-sm font-bold ${isYearly ? 'text-slate-900' : 'text-slate-400'}`}>Hàng năm</span>
              <span className="text-[10px] bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full font-black ">Tiết kiệm 20%</span>
            </div>
          </div>
        </div>

        {/* Pricing Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 items-stretch">
          {PLANS.map((plan) => {
            const cfg = PLAN_CONFIG[plan.code];
            const price = isYearly ? plan.price_yearly / 12 : plan.price_monthly;
            const isEnterprise = plan.code === 'enterprise';

            return (
              <Card key={plan.code} className={`relative flex flex-col p-8 border-2 transition-all duration-300 hover:shadow-2xl hover:-translate-y-2 ${plan.popular ? 'border-emerald-500 shadow-xl scale-105 z-10' : 'border-white hover:border-slate-200'
                }`}>
                {plan.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-emerald-500 text-white text-[10px] font-black  tracking-widest px-4 py-1.5 rounded-full shadow-lg">
                    Phổ biến nhất
                  </div>
                )}

                <div className="mb-8">
                  <p className="text-sm font-black text-slate-400  tracking-widest mb-2">{cfg.label}</p>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black text-slate-900">{formatPrice(price)}</span>
                    {price > 0 && <span className="text-slate-400 font-bold text-sm">/tháng</span>}
                  </div>
                  <p className="text-xs text-slate-400 mt-2 min-h-[40px] leading-relaxed">{plan.description}</p>
                </div>

                <div className="space-y-4 mb-8 flex-1">
                  <div className="pt-6 border-t border-slate-100 space-y-3">
                    <div className="flex items-center justify-between text-xs font-bold">
                      <span className="text-slate-400">Dự án</span>
                      <span className="text-slate-900">{plan.limits.projects === -1 ? 'Không giới hạn' : plan.limits.projects}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-bold">
                      <span className="text-slate-400">Người dùng</span>
                      <span className="text-slate-900">{plan.limits.users === -1 ? 'Không giới hạn' : plan.limits.users}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs font-bold">
                      <span className="text-slate-400">Tài sản</span>
                      <span className="text-slate-900">{plan.limits.assets === -1 ? 'Không giới hạn' : plan.limits.assets.toLocaleString()}</span>
                    </div>
                  </div>

                  <div className="pt-6 border-t border-slate-100 space-y-3">
                    {plan.features.map(fk => {
                      const fl = FEATURE_LABELS[fk];
                      return (
                        <div key={fk} className="flex items-start gap-2.5">
                          <i className="ri-checkbox-circle-fill text-emerald-500 text-sm shrink-0 mt-0.5" />
                          <span className="text-xs text-slate-600 font-medium">{fl?.label || fk}</span>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <Button
                  variant={plan.popular ? 'primary' : 'outline'}
                  className={`w-full h-12 rounded-xl font-black text-xs  tracking-widest ${plan.popular ? 'shadow-lg shadow-emerald-500/20' : ''
                    }`}
                  onClick={() => handleSelectPlan(plan.code)}
                >
                  {plan.code === 'free' ? 'Bắt đầu ngay' : 'Đăng ký gói'}
                </Button>
              </Card>
            );
          })}
        </div>

        {/* Footer info */}
        <div className="mt-20 text-center">
          <p className="text-slate-400 text-sm mb-6 font-medium">Bạn có nhu cầu đặc biệt hơn? Hãy liên hệ đội ngũ chuyên gia của chúng tôi.</p>
          <div className="flex items-center justify-center gap-8">
            <div className="flex items-center gap-2 text-slate-700 font-bold">
              <i className="ri-phone-line text-emerald-600" />
              <span>024.123.4567</span>
            </div>
            <div className="flex items-center gap-2 text-slate-700 font-bold">
              <i className="ri-mail-line text-emerald-600" />
              <span>sales@landscape.vn</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
